import React, { useEffect, useMemo } from "react";
import { IOptionGeneric } from "../../../interface";
import { useUrgentVisitStore } from "../../../../hooks/redux-hooks/urgent-visit-store/urgent-visit.store";
import { NearbyLocationWithSlots } from "../../../../hooks/urgent-visit/useNearbyHospitalSlots";
import { NearbyHospitalCard } from "./NearbyHospitalCard";
import { useNearbyHospitalSlots } from "../../../../hooks/urgent-visit/useNearbyHospitalSlots";
import { useUserStore } from "../../../../hooks/redux-hooks/user-store/user.store";
import Loading from "../../../../components/Loading/Loading";
import { useAppointmentStore } from "../../../../hooks/redux-hooks/appointment-store/appointment.store";
import { injectScheduledSlot } from "./HomeHospitalSlots";

export const NearbyLocationsSection = () => {
  const {
    selectedSlot, selectedLocationId, selectedDate,
    setSelectedSlot, setSelectedLocation, rescheduleAppointmentId,
  } = useUrgentVisitStore();
  const { user } = useUserStore();
  const { appointmentDetails } = useAppointmentStore();
  const { nearbyLocations, loadingLocations, loadNearbyLocations } = useNearbyHospitalSlots();

  useEffect(() => {
    loadNearbyLocations();
  }, [loadNearbyLocations]);

  const homeLocationName = user?.location?.locationName || "";

  const handleNearbySlotSelect = (slot: IOptionGeneric, locationData: NearbyLocationWithSlots) => {
    setSelectedSlot({
      time: slot.value,
      resourcesId: slot.resourcesId,
      label: slot.label,
    });
    setSelectedLocation(
      locationData.location.idLocation,
      locationData.location,
    );
  };

  // Inject disabled slot only for the location
  // where the appointment is currently booked.
  const locationsWithInjectedSlots = useMemo(() => {
    if (!rescheduleAppointmentId || !appointmentDetails) return nearbyLocations;

    const apptLocationId = appointmentDetails.location?.idLocation;
    const selectedDateEpoch = selectedDate ?? Math.floor(Date.now() / 1000);

    return nearbyLocations.map((loc) => {
      if (String(loc.location.idLocation) !== String(apptLocationId)) return loc;

      const timeZone = loc.location.timeZone || "America/Chicago";
      return {
        ...loc,
        timeSlots: injectScheduledSlot(
          loc.timeSlots,
          appointmentDetails.start_at,
          selectedDateEpoch,
          timeZone,
        ),
      };
    });
  }, [nearbyLocations, rescheduleAppointmentId, appointmentDetails, selectedDate]);

  const locationsWithSlots = locationsWithInjectedSlots.filter(
    (loc) => loc.loading || loc.timeSlots.length > 0,
  );

  if (!loadingLocations && locationsWithSlots.length === 0) return null;

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="border-t border-gray-3 pt-4">
        <h4 className="font-semibold text-sm text-light-black leading-5">
          {"Nearby Locations"}
        </h4>
        <p className="text-xs text-gray-1 mt-1">
          {"These hospitals near you have availability for this date."}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {loadingLocations ? (
          <div className="flex w-full justify-center items-center py-8">
            <Loading />
          </div>
        ) : (
          locationsWithSlots.map((locationData) => (
            <NearbyHospitalCard
              key={locationData.location.idLocation}
              data={locationData}
              homeLocationName={homeLocationName}
              selectedSlotValue={selectedSlot?.time || null}
              selectedLocationId={selectedLocationId}
              onSlotSelect={handleNearbySlotSelect}
            />
          ))
        )}
      </div>
    </div>
  );
};
